/**
 * useUrlConversationSync — ADR-095 Phase 3.2.
 *
 * URL is canonical for activeConversationId; URL → store mirror runs
 * automatically; reverse is user-action only via `goToConversation`.
 *
 * The 2026-06-01 loadMessages rehydration block was retired 2026-06-01
 * (same day) when the legacy conversations + messages tables were
 * dropped per operator decision. ADR-091 query_jobs is now the single
 * source-of-truth for thread history. The URL pattern /c/:conversationId
 * survives as a client-side thread-grouping identifier — the UUID
 * flows into query_jobs.request_jsonb.conversation_id at POST time
 * for grouping jobs into thread views (see app/routes/query_jobs.py).
 */

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useChatStore } from "@/runtime/chatStore";

export function useUrlConversationSync(): { goToConversation: (convId: string | null) => void } {
  const params = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);

  useEffect(() => {
    const urlConvId = params.conversationId ?? null;
    // Read the LATEST activeConversationId via getState(), NOT a closed-over
    // selector: a fresh submit / sidebar-restore sets the store id synchronously
    // before navigate('/c/<id>'), but a stale closure value makes the guard
    // spuriously fire setActiveConversation() — which WIPES the just-appended
    // messages (blank main thread). Mismatches (new-thread, switch) still wipe.
    const current = useChatStore.getState().activeConversationId;
    if (urlConvId !== current) {
      setActiveConversation(urlConvId);
    }
  }, [params.conversationId, setActiveConversation]);

  function goToConversation(convId: string | null): void {
    if (convId) {
      navigate(`/c/${convId}`);
    } else {
      navigate("/");
    }
  }

  return { goToConversation };
}
